import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Eye, EyeOff, Lock, Check, X } from 'lucide-react';
import { toast } from '../ui/use-toast';
import supabase from '../../supabase/client';
import { InternalUser } from '../../types';
import { useNavigate } from 'react-router-dom';

interface PasswordResetModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: InternalUser;
  isFirstLogin?: boolean;
}

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

const PasswordResetModal: React.FC<PasswordResetModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  isFirstLogin = false
}) => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const validatePassword = (password: string): PasswordValidation => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
  };

  const validation = validatePassword(newPassword);
  const isPasswordValid = Object.values(validation).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast({
        title: "Erro",
        description: "Todos os campos são obrigatórios.",
        variant: "destructive"
      });
      return;
    }

    if (!isPasswordValid) {
      toast({
        title: "Erro",
        description: "A nova senha não atende aos critérios de segurança.",
        variant: "destructive"
      });
      return;
    }

    if (!passwordsMatch) {
      toast({
        title: "Erro",
        description: "As senhas não coincidem.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      // Usar a função RPC para atualizar a senha do usuário interno
      const { data, error } = await supabase.rpc('update_internal_user_password', {
        p_user_email: currentUser.email,
        p_current_password: currentPassword,
        p_new_password: newPassword
      });

      if (error) {
        toast({
          title: "Erro",
          description: error.message === 'Invalid current password' 
            ? "Senha atual incorreta." 
            : "Erro ao atualizar a senha. Tente novamente.",
          variant: "destructive"
        });
        return;
      }

      if (!data) {
        toast({
          title: "Erro",
          description: "Senha atual incorreta.",
          variant: "destructive"
        });
        return;
      }

      // Atualizar o campo first_login para false após alteração bem-sucedida da senha
      const { error: updateError } = await supabase.rpc('update_first_login_status', {
        p_user_id: currentUser.id
      });

      if (updateError) {
        // Não bloquear o fluxo, apenas logar o erro
      }

      toast({
        title: "Sucesso",
        description: "Senha alterada com sucesso!",
        variant: "default"
      });

      // Resetar campos
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      
      // Fechar modal apenas se a senha foi alterada com sucesso
      onClose();

    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro interno. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const ValidationItem: React.FC<{ isValid: boolean; text: string }> = ({ isValid, text }) => (
    <div className="flex items-center gap-2 text-sm">
      {isValid ? (
        <Check className="h-4 w-4 text-emerald-500" />
      ) : (
        <X className="h-4 w-4 text-destructive" />
      )}
      <span className={isValid ? 'text-emerald-600 dark:text-emerald-400' : 'text-destructive'}>
        {text}
      </span>
    </div>
  );

  const handleModalClose = () => {
    if (isFirstLogin) {
      // Se for primeiro login e tentar fechar sem alterar senha, redirecionar para login
      navigate('/login');
    } else {
      onClose();
    }
  };

  const handleCancelClick = () => {
    if (isFirstLogin) {
      // Se for primeiro login e clicar em cancelar, redirecionar para login
      navigate('/login');
    } else {
      onClose();
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={isFirstLogin ? () => {} : onClose}
    >
      <DialogContent className="sm:max-w-md bg-background border-input">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-foreground">
            <Lock className="h-5 w-5" />
            Alterar Senha
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Senha Atual */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword">Senha Atual</Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Digite sua senha atual"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Nova Senha */}
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nova Senha</Label>
            <div className="relative">
              <Input
                id="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Digite sua nova senha"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Validação da Senha */}
          {newPassword && (
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg border border-input">
              <p className="text-sm font-medium text-foreground">Critérios da senha:</p>
              <div className="space-y-1">
                <ValidationItem isValid={validation.minLength} text="Mínimo 8 caracteres" />
                <ValidationItem isValid={validation.hasUppercase} text="Pelo menos 1 letra maiúscula" />
                <ValidationItem isValid={validation.hasLowercase} text="Pelo menos 1 letra minúscula" />
                <ValidationItem isValid={validation.hasNumber} text="Pelo menos 1 número" />
                <ValidationItem isValid={validation.hasSpecialChar} text="Pelo menos 1 caractere especial" />
              </div>
            </div>
          )}

          {/* Confirmar Senha */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar Nova Senha</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirme sua nova senha"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-sm text-destructive">As senhas não coincidem</p>
            )}
            {confirmPassword && passwordsMatch && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400">Senhas coincidem ✓</p>
            )}
          </div>

          {/* Botões */}
          <div className="flex gap-3 pt-4">
            {!isFirstLogin && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                className="flex-1"
                disabled={isLoading}
              >
                Cancelar
              </Button>
            )}
            {isFirstLogin && (
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                className="flex-1"
                disabled={isLoading}
              >
                Sair
              </Button>
            )}
            <Button
              type="submit"
              className="flex-1"
              disabled={!currentPassword || !isPasswordValid || !passwordsMatch || isLoading}
            >
              {isLoading ? 'Alterando...' : 'Alterar Senha'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordResetModal;